import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FiEdit, FiTrash2, FiSearch, FiChevronLeft, FiChevronRight,
  FiInfo, FiMapPin, FiX, FiUser, FiPhone, FiMail, FiShield,
  FiHeart, FiCalendar, FiCheck, FiAlertCircle, FiCheckSquare,
  FiSquare, FiMinusSquare,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const USERS_PER_PAGE = 8;
const API_BASE = "https://estatebackend.pixelmindsolutions.com";

/* ─── Delete Modal (single) ─── */
const DeleteModal = ({ user, onClose, onConfirm, loading }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-[slideUp_0.28s_cubic-bezier(.22,.68,0,1.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-red-500 to-red-600" />
        <div className="p-6 pb-0 text-center">
          <div className="w-13 h-13 mx-auto mb-4 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-500">
            <FiTrash2 size={22} />
          </div>
          <h3 className="font-serif text-xl font-semibold text-gray-900 mb-2">Delete User?</h3>
          <p className="text-sm text-gray-500 mb-6">
            You're about to permanently remove{" "}
            <span className="font-semibold text-gray-800">{user.name || user.mobile}</span>. This cannot be undone.
          </p>
        </div>
        <div className="flex gap-3 p-6 pt-0">
          <button
            className="flex-1 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-medium shadow-lg shadow-red-200 hover:shadow-red-300 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiTrash2 size={13} />
            )}
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ─── Bulk Delete Modal ─── */
const BulkDeleteModal = ({ count, onClose, onConfirm, loading }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-[slideUp_0.28s_cubic-bezier(.22,.68,0,1.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-red-500 to-red-600" />
        <div className="p-6 pb-0 text-center">
          <div className="w-13 h-13 mx-auto mb-4 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-500">
            <FiTrash2 size={22} />
          </div>
          <h3 className="font-serif text-xl font-semibold text-gray-900 mb-2">Delete {count} Users?</h3>
          <p className="text-sm text-gray-500 mb-6">
            You're about to permanently delete{" "}
            <span className="font-semibold text-gray-800">{count} selected users</span>. This action cannot be undone.
          </p>
        </div>
        <div className="flex gap-3 p-6 pt-0">
          <button
            className="flex-1 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-medium shadow-lg shadow-red-200 hover:shadow-red-300 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiTrash2 size={13} />
            )}
            {loading ? "Deleting…" : `Delete ${count} Users`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ─── User Detail Modal ─── */
const UserDetailModal = ({ user, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const lat = user.location?.coordinates?.[1];
  const lng = user.location?.coordinates?.[0];

  const infoRows = [
    user.email && { icon: <FiMail size={13} />, colorCls: "bg-purple-50 text-purple-600", label: "Email", val: user.email },
    user.mobile && { icon: <FiPhone size={13} />, colorCls: "bg-blue-50 text-blue-600", label: "Mobile", val: user.mobile },
    { icon: <FiShield size={13} />, colorCls: "bg-amber-50 text-amber-600", label: "Role", val: <span className="capitalize">{user.role}</span> },
    { icon: <FiHeart size={13} />, colorCls: "bg-red-50 text-red-500", label: "Wishlist", val: `${user.wishlist?.length || 0} items` },
    lat && lng && {
      icon: <FiMapPin size={13} />, colorCls: "bg-emerald-50 text-emerald-600", label: "Location",
      val: (
        <span>
          {lat.toFixed(5)}, {lng.toFixed(5)}{" "}
          <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-xs ml-1">→ Maps</a>
        </span>
      )
    },
    { icon: <FiCalendar size={13} />, colorCls: "bg-cyan-50 text-cyan-600", label: "Joined", val: new Date(user.createdAt).toLocaleString("en-IN") },
  ].filter(Boolean);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-[slideUp_0.28s_cubic-bezier(.22,.68,0,1.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-red-500 to-red-600" />
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <span className="font-serif text-lg font-semibold text-gray-900">User Details</span>
          <button
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors"
            onClick={onClose}
          >
            <FiX size={14} />
          </button>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-4 mb-5">
            {user.profileImage ? (
              <img src={user.profileImage} alt={user.name} className="w-14 h-14 rounded-full object-cover border-2 border-red-100"
                onError={(e) => { e.target.src = "https://via.placeholder.com/56"; }} />
            ) : (
              <div className="w-14 h-14 rounded-full bg-red-50 border-2 border-red-100 flex items-center justify-center text-red-400">
                <FiUser size={24} />
              </div>
            )}
            <div>
              <div className="font-serif text-xl font-semibold text-gray-900">{user.name || "—"}</div>
              <div className="flex gap-2 mt-1 flex-wrap">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${user.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                  {user.isActive ? "Active" : "Inactive"}
                </span>
                {user.isGoogleUser && <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-600">Google</span>}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-xl overflow-hidden mb-4">
            {infoRows.map((row, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 ${i < infoRows.length - 1 ? "border-b border-gray-100" : ""}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${row.colorCls}`}>
                  {row.icon}
                </div>
                <div>
                  <div className="text-[10px] font-semibold tracking-widest uppercase text-gray-400">{row.label}</div>
                  <div className="text-sm font-medium text-gray-800">{row.val}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-1">Firebase UID</div>
            <div className="text-xs font-mono text-gray-400 break-all">{user.firebaseUid}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ─── Location Modal ─── */
const LocationModal = ({ user, onClose, onSave }) => {
  const [latitude, setLatitude] = useState(user.location?.coordinates?.[1] || "");
  const [longitude, setLongitude] = useState(user.location?.coordinates?.[0] || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleSave = async () => {
    if (!latitude || !longitude) { alert("Latitude & Longitude required"); return; }
    try {
      setSaving(true);
      await axios.put(
        `${API_BASE}/api/auth/update-location/${user._id}`,
        { latitude: Number(latitude), longitude: Number(longitude) },
        { headers: { "Content-Type": "application/json" } }
      );
      onSave();
      onClose();
    } catch {
      alert("Failed to update location");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-[slideUp_0.28s_cubic-bezier(.22,.68,0,1.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-emerald-400 to-emerald-600" />
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <span className="font-serif text-lg font-semibold text-gray-900">Update Location</span>
          <button
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
            onClick={onClose}
          >
            <FiX size={14} />
          </button>
        </div>
        <div className="p-6 pb-0">
          <p className="text-sm text-gray-500 mb-4">
            Editing <span className="font-semibold text-gray-800">{user.name || user.mobile}</span>
          </p>
          <div className="mb-4">
            <label className="block text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-1.5">Latitude</label>
            <input
              type="number"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
              placeholder="e.g. 17.4807459"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
            />
          </div>
          <div className="mb-2">
            <label className="block text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-1.5">Longitude</label>
            <input
              type="number"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
              placeholder="e.g. 78.3940355"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-3 p-6">
          <button
            className="flex-1 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-600 text-white text-sm font-medium shadow-lg shadow-emerald-100 hover:shadow-emerald-200 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiCheck size={13} />
            )}
            {saving ? "Saving…" : "Update Location"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ─── Mobile User Card ─── */
const MobileUserCard = ({ user, index, selected, onSelect, onDetail, onEdit, onLocation, onDelete }) => {
  const lat = user.location?.coordinates?.[1];
  const lng = user.location?.coordinates?.[0];

  return (
    <div className={`bg-white border rounded-2xl p-4 transition-all ${selected ? "border-red-300 shadow-md shadow-red-50" : "border-gray-100 shadow-sm"}`}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button onClick={() => onSelect(user._id)} className="mt-0.5 flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors">
          {selected ? <FiCheckSquare size={18} className="text-red-500" /> : <FiSquare size={18} />}
        </button>

        {/* Avatar */}
        <div className="flex-shrink-0">
          {user.profileImage ? (
            <img src={user.profileImage} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-gray-100"
              onError={(e) => { e.target.src = "https://via.placeholder.com/40"; }} />
          ) : (
            <div className="w-10 h-10 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-red-400">
              <FiUser size={16} />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-gray-900 text-sm truncate">{user.name || "—"}</div>
              {user.mobile && <div className="text-xs text-gray-400 mt-0.5">{user.mobile}</div>}
              {user.email && <div className="text-xs text-gray-400 truncate">{user.email}</div>}
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${user.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-500"}`}>
                {user.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-600 capitalize">{user.role}</span>
            {user.isGoogleUser && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">Google</span>}
            {lat && lng && (
              <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                <FiMapPin size={9} />{lat.toFixed(3)}, {lng.toFixed(3)}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-50">
        <button onClick={() => onDetail(user)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-violet-50 text-violet-600 text-xs font-medium hover:bg-violet-100 transition-colors">
          <FiInfo size={12} /> Info
        </button>
        <button onClick={() => onEdit(user._id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-blue-50 text-blue-600 text-xs font-medium hover:bg-blue-100 transition-colors">
          <FiEdit size={12} /> Edit
        </button>
        <button onClick={() => onLocation(user)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-emerald-50 text-emerald-600 text-xs font-medium hover:bg-emerald-100 transition-colors">
          <FiMapPin size={12} /> Loc
        </button>
        <button onClick={() => onDelete(user)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-medium hover:bg-red-100 transition-colors">
          <FiTrash2 size={12} /> Del
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const UserList = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [detailUser, setDetailUser] = useState(null);
  const [locationUser, setLocationUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/auth/users`);
      setUsers(res.data?.users || []);
    } catch {
      console.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.mobile?.includes(q) ||
        u.role?.toLowerCase().includes(q)
    );
  }, [search, users]);

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * USERS_PER_PAGE;
    return filteredUsers.slice(start, start + USERS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [search]);

  // Selection helpers
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allPageSelected = paginatedUsers.length > 0 && paginatedUsers.every((u) => selectedIds.has(u._id));
  const somePageSelected = paginatedUsers.some((u) => selectedIds.has(u._id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paginatedUsers.forEach((u) => next.delete(u._id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paginatedUsers.forEach((u) => next.add(u._id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Single delete
  const confirmDelete = async () => {
    if (!deleteUser) return;
    try {
      setDeleteLoading(true);
      await axios.delete(`${API_BASE}/api/auth/user/${deleteUser._id}`);
      setDeleteUser(null);
      fetchUsers();
    } catch {
      alert("Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Bulk delete
  const confirmBulkDelete = async () => {
    try {
      setBulkDeleteLoading(true);
      await axios.delete(`${API_BASE}/api/admin/users-delete`, {
        data: { userIds: Array.from(selectedIds) },
        headers: { "Content-Type": "application/json" },
      });
      setSelectedIds(new Set());
      setShowBulkDelete(false);
      fetchUsers();
    } catch {
      alert("Failed to bulk delete users");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  /* Pagination pages */
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);

  const selectedCount = selectedIds.size;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;600&family=DM+Sans:wght@300;400;500&display=swap');
        .font-serif { font-family: 'Lora', Georgia, serif; }
        body { font-family: 'DM Sans', sans-serif; }
        @keyframes slideUp {
          from { opacity:0; transform: translateY(20px) scale(0.98); }
          to   { opacity:1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease; }
      `}</style>

      <div className="min-h-screen bg-[#f7f6f3] px-3 sm:px-6 py-6 sm:py-10">
        <div className="mx-auto bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">

          {/* ── Header ── */}
          <div className="relative px-4 sm:px-8 pt-7 pb-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-red-500 to-red-600" />
            <div>
              <div className="text-[10px] font-semibold tracking-[0.18em] uppercase bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent mb-1.5">
                Admin · People
              </div>
              <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-gray-900 leading-none">Users Management</h1>
              {!loading && (
                <p className="text-xs text-gray-400 mt-1.5">{filteredUsers.length} of {users.length} users shown</p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {!loading && (
                <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-xs font-medium text-red-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  {users.length} Total
                </div>
              )}
              {/* Search */}
              <div className="relative w-full sm:w-72">
                <FiSearch size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-50 transition-all placeholder:text-gray-400"
                  placeholder="Search name, email, mobile…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" onClick={() => setSearch("")}>
                    <FiX size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Bulk Action Bar ── */}
          {selectedCount > 0 && (
            <div className="animate-fadeIn flex items-center justify-between gap-3 px-4 sm:px-8 py-3 bg-red-50 border-b border-red-100 flex-wrap">
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-semibold text-red-700">{selectedCount} user{selectedCount > 1 ? "s" : ""} selected</span>
              </div>
              <div className="flex items-center gap-2.5">
                <button
                  className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                  onClick={clearSelection}
                >
                  Clear
                </button>
                <button
                  className="flex items-center gap-1.5 text-xs font-semibold text-white px-4 py-1.5 rounded-lg bg-gradient-to-r from-red-500 to-red-600 shadow-sm hover:shadow-md transition-all"
                  onClick={() => setShowBulkDelete(true)}
                >
                  <FiTrash2 size={12} /> Delete {selectedCount} Selected
                </button>
              </div>
            </div>
          )}

          {/* ── Desktop Table ── */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {/* Select all checkbox */}
                  <th className="px-4 py-3 w-10">
                    <button onClick={toggleSelectAll} className="text-gray-400 hover:text-red-500 transition-colors">
                      {allPageSelected ? (
                        <FiCheckSquare size={16} className="text-red-500" />
                      ) : somePageSelected ? (
                        <FiMinusSquare size={16} className="text-red-400" />
                      ) : (
                        <FiSquare size={16} />
                      )}
                    </button>
                  </th>
                  {["#", "User", "Mobile", "Email", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-gray-400 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9">
                      <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-8 h-8 border-[3px] border-red-100 border-t-red-500 rounded-full animate-spin mb-3" />
                        <span className="text-xs text-gray-400">Loading users…</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-400 mb-3">
                          <FiAlertCircle size={22} />
                        </div>
                        <div className="font-serif text-lg font-semibold text-gray-800 mb-1">No users found</div>
                        <div className="text-xs text-gray-400">Try adjusting your search query</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user, index) => {
                    const lat = user.location?.coordinates?.[1];
                    const lng = user.location?.coordinates?.[0];
                    const isSelected = selectedIds.has(user._id);

                    return (
                      <tr
                        key={user._id}
                        className={`border-b border-gray-50 transition-colors ${isSelected ? "bg-red-50/60" : "hover:bg-gray-50/60"}`}
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-3.5">
                          <button onClick={() => toggleSelect(user._id)} className="text-gray-300 hover:text-red-400 transition-colors">
                            {isSelected ? <FiCheckSquare size={16} className="text-red-500" /> : <FiSquare size={16} />}
                          </button>
                        </td>

                        {/* # */}
                        <td className="px-4 py-3.5">
                          <span className="text-[11px] font-mono text-gray-400">
                            {(currentPage - 1) * USERS_PER_PAGE + index + 1}
                          </span>
                        </td>

                        {/* User */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2.5 min-w-[140px]">
                            {user.profileImage ? (
                              <img src={user.profileImage} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-gray-100 flex-shrink-0"
                                onError={(e) => { e.target.src = "https://via.placeholder.com/36"; }} />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-red-400 flex-shrink-0">
                                <FiUser size={15} />
                              </div>
                            )}
                            <div>
                              <div className="font-semibold text-gray-900 text-[13px] leading-tight">{user.name || "—"}</div>
                              {user.isGoogleUser && <div className="text-[10px] text-blue-500 font-medium">Google</div>}
                            </div>
                          </div>
                        </td>

                        {/* Mobile */}
                        <td className="px-4 py-3.5 text-[13px] text-gray-700 whitespace-nowrap">{user.mobile || "—"}</td>

                        {/* Email */}
                        <td className="px-4 py-3.5 max-w-[180px]">
                          <span className="block text-[12px] text-gray-400 truncate">{user.email || "—"}</span>
                        </td>

                        {/* Location */}
                        {/* <td className="px-4 py-3.5">
                          {lat && lng ? (
                            <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium hover:underline whitespace-nowrap">
                              <FiMapPin size={11} />
                              {lat.toFixed(4)}, {lng.toFixed(4)}
                            </a>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td> */}

                        {/* Status */}
                        <td className="px-4 py-3.5">
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${user.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-500"}`}>
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <button title="View Details" onClick={() => setDetailUser(user)}
                              className="w-8 h-8 rounded-[9px] border border-violet-200 bg-violet-50 text-violet-600 flex items-center justify-center hover:bg-violet-100 transition-all hover:scale-105">
                              <FiInfo size={13} />
                            </button>
                            <button title="Edit" onClick={() => navigate(`/admin/users/update/${user._id}`)}
                              className="w-8 h-8 rounded-[9px] border border-blue-200 bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-all hover:scale-105">
                              <FiEdit size={13} />
                            </button>
                            <button title="Update Location" onClick={() => setLocationUser(user)}
                              className="w-8 h-8 rounded-[9px] border border-emerald-200 bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100 transition-all hover:scale-105">
                              <FiMapPin size={13} />
                            </button>
                            <button title="Delete" onClick={() => setDeleteUser(user)}
                              className="w-8 h-8 rounded-[9px] border border-red-200 bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-all hover:scale-105">
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── Mobile Card List ── */}
          <div className="md:hidden">
            {/* Select all mobile */}
            {!loading && paginatedUsers.length > 0 && (
              <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                <button onClick={toggleSelectAll} className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-red-500 transition-colors">
                  {allPageSelected ? <FiCheckSquare size={15} className="text-red-500" /> : somePageSelected ? <FiMinusSquare size={15} className="text-red-400" /> : <FiSquare size={15} />}
                  {allPageSelected ? "Deselect all" : "Select all on page"}
                </button>
                <span className="text-xs text-gray-400">{paginatedUsers.length} on page</span>
              </div>
            )}

            <div className="p-4 space-y-3">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-8 h-8 border-[3px] border-red-100 border-t-red-500 rounded-full animate-spin mb-3" />
                  <span className="text-xs text-gray-400">Loading users…</span>
                </div>
              ) : paginatedUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-400 mb-3">
                    <FiAlertCircle size={22} />
                  </div>
                  <div className="font-serif text-lg font-semibold text-gray-800 mb-1">No users found</div>
                  <div className="text-xs text-gray-400">Try adjusting your search query</div>
                </div>
              ) : (
                paginatedUsers.map((user, index) => (
                  <MobileUserCard
                    key={user._id}
                    user={user}
                    index={(currentPage - 1) * USERS_PER_PAGE + index}
                    selected={selectedIds.has(user._id)}
                    onSelect={toggleSelect}
                    onDetail={setDetailUser}
                    onEdit={(id) => navigate(`/admin/users/update/${id}`)}
                    onLocation={setLocationUser}
                    onDelete={setDeleteUser}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-8 py-5 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-gray-400">Page {currentPage} of {totalPages}</div>
              <div className="flex items-center gap-1.5">
                <button
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl border border-gray-200 bg-transparent flex items-center justify-center text-gray-600 hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <FiChevronLeft size={14} />
                </button>
                {pageNumbers.map((p, i) =>
                  p === "..." ? (
                    <span key={`d${i}`} className="text-sm text-gray-400 px-1">…</span>
                  ) : (
                    <button
                      key={p}
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl border text-[13px] font-medium flex items-center justify-center transition-all ${
                        currentPage === p
                          ? "bg-gradient-to-br from-red-500 to-red-600 border-transparent text-white shadow-md shadow-red-200"
                          : "border-gray-200 text-gray-600 hover:bg-red-50 hover:border-red-200"
                      }`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl border border-gray-200 bg-transparent flex items-center justify-center text-gray-600 hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <FiChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {detailUser   && <UserDetailModal user={detailUser}   onClose={() => setDetailUser(null)} />}
      {locationUser && <LocationModal   user={locationUser} onClose={() => setLocationUser(null)} onSave={fetchUsers} />}
      {deleteUser   && <DeleteModal     user={deleteUser}   onClose={() => setDeleteUser(null)} onConfirm={confirmDelete} loading={deleteLoading} />}
      {showBulkDelete && (
        <BulkDeleteModal
          count={selectedCount}
          onClose={() => setShowBulkDelete(false)}
          onConfirm={confirmBulkDelete}
          loading={bulkDeleteLoading}
        />
      )}
    </>
  );
};

export default UserList;